<template>
  <div ref="tabsRoot" class="video-guest-tabs" data-testid="video-guest-tabs">
    <Tabs id="video-guest-tabs" :selected-tab="selectedTab" @tab-click="onTabClick" @changed="(e: any) => onTabChange(e.tab.hash)">
      <!-- Summary Tab (default) -->
      <Tab id="summary" :hidden="hideSummary || !hasSummary" name="Summary" class="video-guest-tabs__tab">
        <div class="video-guest-tabs__summary">
          <SummaryContent />
        </div>
      </Tab>

      <!-- To-Do Tab: read-only for viewers, the owner checks rows off -->
      <Tab id="to-do" :hidden="!isToDoAvailable" :name="toDoTabLabel" class="video-guest-tabs__tab">
        <div class="video-guest-tabs__to-do">
          <ToDoContent />
        </div>
      </Tab>

      <!-- Comments Tab -->
      <Tab id="comments" :hidden="!commentsEnabled" name="Comments" class="video-guest-tabs__tab">
        <div class="video-guest-tabs__comments">
          <Comments @addedComment="emit('addedComment')" />
        </div>
      </Tab>

      <!-- Chapters Tab -->
      <Tab v-if="showTheaterChapters" id="chapters" name="Chapters" class="video-guest-tabs__tab">
        <GuestChaptersSidePanel />
      </Tab>

      <!-- Transcript Tab -->
      <Tab id="transcript" :hidden="!transcriptionTabEnabled" name="Transcript" class="video-guest-tabs__tab">
        <Transcription />
      </Tab>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { Tab, Tabs } from '@/storybook';

import Comments from '@/modules/viewer/components/item_tabs/comments/Comments.vue';
import Transcription from '@/modules/viewer/components/item_tabs/transcription/Transcription.vue';
import SummaryContent from '@/modules/viewer/components/SummaryContent.vue';
import GuestChaptersSidePanel from './GuestChaptersSidePanel.vue';
import ToDoContent from '@/modules/viewer/components/ToDoContent.vue';

import { useViewerItemStore, ItemType } from '@/modules/viewer/store/useViewerItemStore';
import { useTranscriptionStore, ItemTranscriptionStatus } from '@/modules/viewer/store/useTranscriptionStore';
import { useTabNavigation } from '@/modules/viewer/composables/useTabNavigation';
import { useActiveTabListener } from '@/modules/viewer/composables/useActiveTab';
import { useToDo } from '@/modules/viewer/composables/useToDo';

const props = withDefaults(
  defineProps<{
    hideSummary?: boolean;
    showTheaterChapters?: boolean;
  }>(),
  { hideSummary: false, showTheaterChapters: false }
);

const emit = defineEmits<{
  addedComment: [];
}>();

const store = useViewerItemStore();
const transcriptionStore = useTranscriptionStore();
const tabsRoot = ref<HTMLElement | null>(null);

const { type, commentsEnabled, description } = storeToRefs(store);
const { transcriptionStatus } = storeToRefs(transcriptionStore);

const { toDoTabLabel, isToDoAvailable } = useToDo({ badgeClass: 'video-guest-tabs__todo-badge' });

const hasSummary = computed(() => !!description.value && description.value.length > 0);

const transcriptionTabEnabled = computed(() => {
  return (
    type.value === ItemType.VIDEO &&
    transcriptionStatus.value &&
    [ItemTranscriptionStatus.DONE, ItemTranscriptionStatus.EDITED].includes(transcriptionStatus.value)
  );
});

const availableTabs = computed(() => {
  const tabs: string[] = [];
  if (hasSummary.value && !props.hideSummary) {
    tabs.push('#summary');
  }
  if (isToDoAvailable.value) {
    tabs.push('#to-do');
  }
  if (commentsEnabled.value) {
    tabs.push('#comments');
  }
  if (props.showTheaterChapters) {
    tabs.push('#chapters');
  }
  if (transcriptionTabEnabled.value) {
    tabs.push('#transcript');
  }
  return tabs;
});

const { selectedTab, onTabClick, onTabChange, selectTab } = useTabNavigation(availableTabs, tabsRoot, '#summary');

// Listen for external tab switch requests (from EngagementBar)
const { activeTabOverride } = useActiveTabListener();
watch(activeTabOverride, tab => {
  if (tab) {
    selectTab(tab);
    activeTabOverride.value = null;
  }
});
</script>

<style scoped>
.video-guest-tabs {
  @apply mt-4;
}

.video-guest-tabs__tab {
  @apply mt-3 sm:mt-4;
  min-height: 200px;
}

@media (min-width: 640px) {
  .video-guest-tabs__tab {
    min-height: 300px;
  }
}

.video-guest-tabs__summary {
  @apply relative flex flex-col p-3 sm:p-4 sm:border sm:border-slate-300 sm:dark:border-zinc-700 bg-white dark:bg-zinc-900 sm:rounded-10;
}

.video-guest-tabs__to-do {
  @apply relative flex flex-col p-3 sm:p-4 sm:border sm:border-slate-300 sm:dark:border-zinc-700 bg-white dark:bg-zinc-900 sm:rounded-10;
}

/* Matches BaseBadge.vue - can't reuse the component itself since this
   markup is injected via the tabs library's innerHTML, outside Vue's
   render tree. */
:deep(.video-guest-tabs__todo-badge) {
  @apply ml-1 inline-block rounded-full bg-zinc-100 px-1.5 text-sm text-zinc-500 dark:bg-zinc-600 dark:text-zinc-400;
}

.video-guest-tabs__comments {
  @apply relative flex flex-col p-3 sm:p-4 sm:border sm:border-slate-300 sm:dark:border-zinc-700 bg-white dark:bg-zinc-900 sm:rounded-10;
}
</style>
